import type { ReactElement } from "react";
import { motion, type MotionStyle } from "framer-motion";
import { useMeasuredInsertion } from "@primitives/useMeasuredInsertion";

/**
 * List row wrapper with the token insertion motion. The board's lists have
 * no exit motion, so only `initial`/`animate`/`transition` are used. A new
 * row is measured once and grows to that px (`useMeasuredInsertion`). Clips
 * only while the row grows in, so a focus ring is never cut afterwards.
 * `gap` is inner padding, not margin, so it grows with the row.
 */
export function InsertedRow({
  isNew,
  children,
  gap = 0,
  style,
}: {
  readonly isNew: boolean;
  readonly children: ReactElement | null;
  /** Vertical space below the row, in px. */
  readonly gap?: number;
  readonly style?: MotionStyle;
}): ReactElement {
  const { motionProps, contentRef, growing } = useMeasuredInsertion(isNew);
  return (
    <motion.div
      initial={motionProps.initial}
      animate={motionProps.animate}
      transition={motionProps.transition}
      onAnimationComplete={motionProps.onAnimationComplete}
      style={{ overflow: growing ? "hidden" : "visible", flexShrink: 0, minWidth: 0, ...style }}
    >
      <div ref={contentRef} style={{ paddingBottom: gap }}>
        {children}
      </div>
    </motion.div>
  );
}
